import styled from '@emotion/styled'
import { withClass } from 'common/emotion'
import { colors } from 'component-library/__styling/settings'
import { IconPremium } from 'component-library/icons/IconPremium/IconPremium'
import { calcRem, breakpoint } from 'component-library/__styling/style-functions'
import { visuallyhidden, transition } from 'component-library/__styling/style-mixins'
import { WebNavLink } from 'common/navigation/web-link'

export const StyledBreachContainer = withClass('BreachNotification_StyledBreachContainer')(
    styled('div')(({ theme }) => ({
        padding: calcRem(theme.margins.md),
        textAlign: 'center',
        backgroundColor: colors.thewest.breachNavy,

        [breakpoint('sm')]: {
            padding: calcRem(theme.margins.lg),
        },
    }))
)

export const StyledHeading = withClass('BreachNotification_StyledHeading')(
    styled('h2')(visuallyhidden)
)

export const StyledPremiumIcon = styled(IconPremium)(({ theme }) => ({
    display: 'block',
    width: calcRem(24),
    height: calcRem(24),
    transform: 'translateY(2px)',
    margin: `0 auto ${calcRem(theme.margins.sm)}`,

    [breakpoint('sm')]: {
        display: 'inline-block',
        width: calcRem(16),
        height: calcRem(16),
        margin: 0,
        marginRight: calcRem(theme.margins.xs),
    },
}))

export const StyledPremiumLabel = withClass('BreachNotification_StyledPremiumLabel')(
    styled('div')(({ theme }) => ({
        lineHeight: 1.5,
        color: colors.white,
        maxWidth: calcRem(600),
        margin: '0 auto',
        marginBottom: calcRem(theme.margins.sm),
    }))
)

export const StyledCTA = withClass('BreachNotification_StyledCTA')(
    styled('p')(({ theme }) => ({
        margin: calcRem(theme.margins.md, 0),
        color: colors.white,
        fontSize: calcRem(24),
        fontWeight: 700,
        lineHeight: 1.25,

        [breakpoint('md')]: {
            margin: calcRem(theme.margins.md, 0, theme.margins.sm),
            fontSize: calcRem(24),
            lineHeight: 1.5,
        },
    }))
)

export const StyledSubscriptionContainer = styled('div')(({ theme }) => ({
    [breakpoint('xs')]: {
        margin: calcRem(0, -theme.margins.sm),
    },

    [breakpoint('sm')]: {
        display: 'flex',
    },
}))

export const StyledSubscription = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: '100%',
    padding: 0,
    marginBottom: calcRem(theme.margins.md),

    [breakpoint('xs')]: {
        flexBasis: '50%',
        padding: calcRem(0, theme.margins.sm),
    },

    [breakpoint('sm')]: {
        marginBottom: 0,
    },
}))

export const StyledImageEveryDayDigital = withClass(
    'BreachNotification_StyledImageEveryDayDigital'
)(
    styled('img')({
        width: 200,
        margin: '24px 0 -27px',
        zIndex: 1,

        [breakpoint('lg')]: {
            width: 'auto',
            maxWidth: 250,
            margin: '26px 0 -30px',
        },
    })
)

export const StyledImageNewsPaper = withClass('BreachNotification_StyledImageNewsPaper')(
    styled('img')({
        width: 200,
        margin: '0 0 -24px',
        zIndex: 1,

        [breakpoint('lg')]: {
            width: 'auto',
            maxWidth: 250,
            margin: '0 0 -30px',
        },
    })
)

export const StyledSubscriptionWrapper = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexGrow: 1,
    width: '100%',
    padding: calcRem(theme.margins.md, theme.margins.md),
    backgroundColor: colors.white,
}))

export const StyledSubscriptionHeading = styled('h3')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    margin: calcRem(theme.margins.sm, 0),
    color: colors.black,
    fontSize: calcRem(22),
    fontFamily: theme.fonts.sansSerifCond,
    lineHeight: 1.2,

    [breakpoint('xs')]: {
        margin: calcRem(theme.margins.md, 0),
    },

    [breakpoint('sm')]: {
        minHeight: calcRem(94), // fontSize * lineHeight * expectedLines
    },

    [breakpoint('md')]: {
        fontSize: calcRem(26),
    },
}))

export const StyledParagraph = styled('p')({
    // IE11 specific to wrap text
    width: '100%',
    fontSize: calcRem(13),
    margin: 'auto 0',
})

export const StyledSubscriptionPrice = styled('span')({
    display: 'block',
    color: colors.thewest.breachNavy,
})

export const StyledSubscribeButton = withClass('BreachNotification_StyledSubscribeButton')(
    styled('button')(({ theme }) => ({
        display: 'block',
        flexGrow: 0,
        padding: calcRem(theme.margins.sm, theme.margins.lg),
        marginBottom: calcRem(theme.margins.md),
        backgroundColor: colors.thewest.hollywoodCerise,
        borderRadius: calcRem(2),
        border: 'none',
        color: colors.white,
        fontFamily: theme.fonts.sansSerif,
        fontWeight: 700,
        fontSize: calcRem(14),
        lineHeight: 1.5,
        textDecoration: 'none',
        textTransform: 'uppercase',
        cursor: 'pointer',
        ...transition({ properties: ['background', 'color'] }),

        '&:hover, &:focus': {
            backgroundImage: 'none',
            outline: 'none',
        },

        '&:hover': {
            backgroundColor: theme.sectionValues.primaryColor,
        },

        '&:focus': {
            backgroundColor: colors.thewest.greyKoala,
            textDecoration: 'underline',
        },
    }))
)

export const StyledLoginContainer = styled('p')({
    margin: 0,
    color: colors.white,
})

export const StyledLogInButton = styled('button')({
    padding: 0,
    margin: 0,
    background: 'none',
    border: 'none',
    color: colors.white,
    cursor: 'pointer',
    fontWeight: 700,
    textDecoration: 'underline',
    ...transition({ properties: ['color'] }),

    '&:hover, &:focus': {
        color: colors.thewest.hollywoodCerise,
    },
})

export const StyledLogInLink = StyledLogInButton.withComponent(WebNavLink)

export const StyledBreachFooter = withClass('BreachNotification_StyledBreachFooter')(
    styled('footer')({
        color: colors.white,
        textAlign: 'center',
    })
)

export const StyledBreachFooterText = styled('p')({
    fontSize: calcRem(18),
    fontWeight: 'bold',
})

export const StyledHR = styled('hr')(({ theme }) => ({
    height: calcRem(1),
    backgroundColor: colors.white,
    border: 0,
    margin: theme.margins.md,
}))

export const StyledOutlineAnchor = styled('button')(({ theme }) => ({
    display: 'inline-block',
    padding: calcRem(theme.margins.sm, theme.margins.md),
    marginTop: calcRem(theme.margins.md),
    color: colors.white,
    background: 'none',
    border: `${calcRem(1)} solid ${colors.white}`,
    borderRadius: calcRem(2),
    fontFamily: theme.fonts.sansSerif,
    fontWeight: 700,
    lineHeight: 1.5,
    textDecoration: 'none',
    textTransform: 'uppercase',
    cursor: 'pointer',
    ...transition({ properties: ['background', 'color'] }),

    '&:hover': {
        backgroundColor: colors.white,
        color: colors.thewest.greyGorilla,
    },

    '&:focus': {
        backgroundColor: colors.thewest.greyKoala,
        borderColor: colors.thewest.greyKoala,
        outline: 'none',
        textDecoration: 'underline',
    },
}))

export const StyledContactDetails = styled('p')(({ theme }) => ({
    marginTop: calcRem(theme.margins.lg),
    marginBottom: 0,
    fontWeight: 700,
    fontFamily: theme.fonts.sansSerif,
    textAlign: 'center',

    a: {
        color: colors.white,
        ...transition({ properties: ['color'] }),
        fontWeight: 700,
    },

    'a:hover, a:focus': {
        color: colors.thewest.hollywoodCerise,
    },
}))
